import React, { useEffect, useRef, useMemo, useState } from 'react';
import { Chart } from '@antv/g2';
import { ConfigProvider } from 'antd';
import { useTheme } from '../../theme/ThemeContext';
import Select from '../../atoms/Select/Select';
import Button from '../../atoms/Button/Button';
import DownloadFile from '../../atoms/Icon/DownloadFile';
import ArrowRight from '../../atoms/Icon/ArrowRight';
import { WaterfallChartProps, ProcessedWaterfallData, WaterfallDimension } from './WaterfallChart.types';
import {
    getWaterfallSelectionResetKey,
    processWaterfallData,
    resolveWaterfallDefaultDimension,
    resolveWaterfallDimensionOptions,
    resolveWaterfallMetricOptions,
    resolveWaterfallYAxisTitle,
} from './waterfallDataUtils';
import { CHART_FONT_FAMILY_CSS } from '../../../utils/formatters';
import { getCategoryAxisLabelTransforms } from '../../../utils/chartAxisLabelTransforms';
import { attachAxisLabelHoverTooltip } from '../../../utils/chartAxisLabelHoverTooltip';
import {
    buildWaterfallConnectorData,
    computeWaterfallSlotLayout,
    computeWaterfallValueDomain,
    WATERFALL_BAR_WIDTH_PX,
    WATERFALL_MIN_SLOT_WIDTH_PX,
    WATERFALL_SCROLLBAR_RESERVE_PX,
    WATERFALL_STACKED_LABEL_HEADROOM_PX,
    WATERFALL_VALUE_LABEL_HEADROOM_PX,
} from './waterfallLayoutUtils';

const WATERFALL_X_AXIS_SIZE = 100;

/** Canvas `paddingTop`, keeping the topmost y-axis tick label clear of the card edge. */
const WATERFALL_PADDING_TOP = 40;

/** Combined top + bottom padding in px of the chart card around the canvas (`py-5`). */
const WATERFALL_CHART_VERTICAL_PADDING_PX = 40;

const WaterfallChart: React.FC<WaterfallChartProps> = (props) => {
    const {
        config,
        title,
        description,
        filters = [],
        footerLabel,
        showFooterArrow = true,
        onDownload,
        onBarClick,
        height = 400,
        positiveColor = '#52C41A',
        negativeColor = '#FF4D4F',
        totalColor = '#5B50D1',
        connectorColor = 'rgba(0, 0, 0, 0.25)',
        className = '',
        chartClassName = '',
        showLabels = true,
        showConnectors = true,
        showPercentages = true,
    } = props;

    const theme = useTheme();
    const chartContainer = useRef<HTMLDivElement | null>(null);
    const chartInstance = useRef<Chart | null>(null);
    /** Detaches the full-name hover tooltip on wrapped x-axis tick labels. */
    const detachAxisLabelTooltip = useRef<(() => void) | null>(null);
    const [chartWrapperWidth, setChartWrapperWidth] = useState(0);

    const { viz_data } = config;
    const chartTitle = title || viz_data.chartTitle;

    const metricOptions = useMemo(() => resolveWaterfallMetricOptions(viz_data), [viz_data]);
    const dimensionOptions = useMemo(() => resolveWaterfallDimensionOptions(viz_data), [viz_data]);
    const showMetricDropdown = metricOptions.length > 1;
    const showDimensionDropdown = dimensionOptions.length > 1;

    const [selectedMetricIndex, setSelectedMetricIndex] = useState(metricOptions[0]?.value ?? 0);
    const [selectedDimension, setSelectedDimension] = useState<WaterfallDimension>(() =>
        resolveWaterfallDefaultDimension(viz_data, dimensionOptions),
    );

    const selectionResetKey = getWaterfallSelectionResetKey(viz_data);

    useEffect(() => {
        setSelectedMetricIndex(metricOptions[0]?.value ?? 0);
        setSelectedDimension(resolveWaterfallDefaultDimension(viz_data, dimensionOptions));
        // Reset only when chart payload content changes, not on viz_data reference churn.
        // eslint-disable-next-line react-hooks/exhaustive-deps -- metric/dimension options are derived from selectionResetKey
    }, [selectionResetKey]);

    const yAxisTitle = useMemo(
        () => resolveWaterfallYAxisTitle(viz_data, selectedMetricIndex),
        [viz_data, selectedMetricIndex],
    );

    const processedData = useMemo(
        (): ProcessedWaterfallData[] =>
            processWaterfallData(viz_data, selectedMetricIndex, selectedDimension),
        [viz_data, selectedMetricIndex, selectedDimension],
    );

    useEffect(() => {
        const wrapper = chartContainer.current;
        if (!wrapper) return;

        const updateWidth = () => {
            setChartWrapperWidth(wrapper.getBoundingClientRect().width);
        };

        updateWidth();
        const resizeObserver = new ResizeObserver(updateWidth);
        resizeObserver.observe(wrapper);

        return () => {
            resizeObserver.disconnect();
        };
    }, []);

    /**
     * Horizontal geometry: a fixed 16px bar centered in a slot of at least 72px, scrolling rather
     * than compressing once the bars outgrow the plot area.
     */
    const slotLayout = useMemo(
        () => computeWaterfallSlotLayout(processedData.length, chartWrapperWidth),
        [chartWrapperWidth, processedData.length],
    );

    const connectorData = useMemo(
        () => (showConnectors ? buildWaterfallConnectorData(processedData) : []),
        [processedData, showConnectors],
    );

    /**
     * Vertical geometry: the value scale is padded so the label stack above the tallest bar sits
     * inside the plot area. Canvas padding alone is not enough — while scrolling, G2's scrollbar
     * filter clips the view to the plot area and trims anything drawn above it.
     */
    const valueDomain = useMemo(() => {
        if (!showLabels) return undefined;

        const paddingBottom = WATERFALL_X_AXIS_SIZE + (slotLayout.needsScroll ? WATERFALL_SCROLLBAR_RESERVE_PX : 0);
        const plotHeightPx = height - WATERFALL_CHART_VERTICAL_PADDING_PX - WATERFALL_PADDING_TOP - paddingBottom;

        return computeWaterfallValueDomain(
            processedData,
            plotHeightPx,
            showPercentages ? WATERFALL_STACKED_LABEL_HEADROOM_PX : WATERFALL_VALUE_LABEL_HEADROOM_PX,
        );
    }, [height, processedData, showLabels, showPercentages, slotLayout.needsScroll]);

    // Render chart
    useEffect(() => {
        if (!chartContainer.current) return;

        // Destroy previous chart instance
        if (chartInstance.current) {
            chartInstance.current.destroy();
        }

        // Create new chart with top padding so labels above bars are not clipped
        const chart = new Chart({
            container: chartContainer.current,
            autoFit: true,
            paddingTop: WATERFALL_PADDING_TOP,
            paddingBottom:
                WATERFALL_X_AXIS_SIZE + (slotLayout.needsScroll ? WATERFALL_SCROLLBAR_RESERVE_PX : 0),
        });

        chart.coordinate({ type: 'cartesian' });

        // Add data
        chart.data(
            processedData.map((d, i) => ({
                ...d,
                index: i,
            })),
        );

        // `nice` only ever widens a domain, so the label headroom survives the tick rounding.
        chart.scale('y', { nice: true, ...(valueDomain ? { domain: valueDomain } : {}) });

        // Add interval (bars) for the waterfall effect.
        // G2 v5 range intervals require field-name array channels (y/y1), not a callback returning [start, end].
        const barMark = chart
            .interval()
            .encode('x', 'index')
            .encode('y', ['end', 'start'])
            .encode('color', (d: ProcessedWaterfallData) => {
                if (d.type === 'total') return 'total';
                if (d.type === 'increase') return 'increase';
                return 'decrease';
            })
            /**
             * Zero band padding makes each slot exactly `plotWidth / visibleBars`, which is what lets
             * the slot midpoint, the band midpoint and the x-axis tick position coincide. The bar is
             * not sized by the band: `minWidth`/`maxWidth` clamp it to a constant 16px, and G2's rect
             * shape re-centers the clamped bar on the band midpoint. All slack therefore lands in the
             * gaps rather than in the bar.
             */
            .scale('x', { type: 'band', paddingInner: 0, paddingOuter: 0 })
            .scale('color', {
                type: 'ordinal',
                domain: ['increase', 'decrease', 'total'],
                range: [positiveColor, negativeColor, totalColor],
            })
            .style('radius', 2)
            .style('minWidth', WATERFALL_BAR_WIDTH_PX)
            .style('maxWidth', WATERFALL_BAR_WIDTH_PX)
            .axis('x', {
                title: false,
                size: WATERFALL_X_AXIS_SIZE,
                /**
                 * The formatter only resolves the slot index back to its category name; wrapping and
                 * ellipsising are left to G2, which measures the rendered labels and shrinks them until
                 * they no longer overlap. `labelAuto*` flags are absent on purpose — a non-empty
                 * `transform` makes G2 ignore them.
                 */
                labelFormatter: (text: string) => {
                    const idx = Number(text);
                    return !Number.isNaN(idx) && processedData[idx] ? processedData[idx].category : text;
                },
                transform: getCategoryAxisLabelTransforms({ tickSpacePx: WATERFALL_MIN_SLOT_WIDTH_PX }),
                labelSpacing: 4,
                labelFontSize: 13,
                labelFontFamily: CHART_FONT_FAMILY_CSS,
                labelStroke: '#000',
                labelOpacity: 0.4,
                labelFontWeight: 400,
            })
            .axis('y', {
                title: yAxisTitle,
                titleSpacing: 20,
                grid: true,
                gridStroke: 'rgba(0, 0, 0, 0.06)',
                gridLineWidth: 1,
                labelFontFamily: CHART_FONT_FAMILY_CSS,
                labelFormatter: (value: number) => {
                    const abs = Math.abs(value);
                    if (abs >= 1_000_000_000) {
                        return `${(value / 1_000_000_000).toFixed(2)}B`;
                    }
                    if (abs >= 1_000_000) {
                        return `${(value / 1_000_000).toFixed(2)}M`;
                    }
                    if (abs >= 1_000) {
                        return `${(value / 1_000).toFixed(2)}K`;
                    }
                    return value.toFixed(0);
                },
            })
            .legend(false)
            .tooltip({
                title: (d: any) => d.category,
                items: [
                    (d: ProcessedWaterfallData) => ({
                        name: 'Value',
                        value: d.value.toLocaleString(),
                    }),
                    (d: ProcessedWaterfallData) => ({
                        name: 'Range',
                        value: `${d.start.toLocaleString()} → ${d.end.toLocaleString()}`,
                    }),
                ],
            });

        /**
         * Only attach the scrollbar when the bars actually overflow. Passing a scrollbar config while
         * everything fits still makes G2 run its scrollbar-aware layout pass, which mis-sizes the
         * value-axis viewport on the first render until the user nudges the scrollbar.
         */
        if (slotLayout.needsScroll) {
            /**
             * G2's scrollbar filter defaults to `adaptiveMode: 'filter'`, which refits the value
             * domain to the visible window on every drag. That silently replaces the padded domain
             * above, so the tallest bar in view is pinned to the top of the plot and its label is
             * trimmed by the `clip: true` the same filter applies. Turning it off also keeps bar
             * heights comparable across scroll positions instead of rescaling the axis under the
             * user.
             */
            chart.interaction('scrollbarFilter', { adaptiveMode: false });

            barMark.scrollbar('x', {
                ratio: slotLayout.scrollbarRatio,
                trackSize: 8,
                thumbFill: '#5B50D1',
                thumbFillOpacity: 0.45,
                trackFill: '#f0f0f0',
                trackFillOpacity: 1,
                isRound: true,
            });
        }

        // Add labels on top of bars
        if (showLabels) {
            chart
                .text()
                .encode('x', 'index')
                .encode('y', (d: ProcessedWaterfallData) => Math.max(d.start, d.end))
                .encode('text', (d: ProcessedWaterfallData) => {
                    const absValue = Math.abs(d.value);
                    let formattedValue = '';

                    if (absValue >= 1_000_000_000) {
                        formattedValue = `${(absValue / 1_000_000_000).toFixed(2)}B`;
                    } else if (absValue >= 1_000_000) {
                        formattedValue = `${(absValue / 1_000_000).toFixed(2)}M`;
                    } else if (absValue >= 1_000) {
                        formattedValue = `${(absValue / 1_000).toFixed(2)}K`;
                    } else {
                        formattedValue = absValue.toFixed(0);
                    }

                    return formattedValue;
                })
                .style('textAlign', 'center')
                .style('textBaseline', 'bottom')
                .style('dy', -5)
                .style('fontSize', 12)
                .style('fill', 'rgba(0, 0, 0, 0.65)')
                .style('fontFamily', CHART_FONT_FAMILY_CSS);

            // Add percentage labels if enabled
            if (showPercentages) {
                chart
                    .text()
                    .encode('x', 'index')
                    .encode('y', (d: ProcessedWaterfallData) => Math.max(d.start, d.end))
                    .encode('text', (d: ProcessedWaterfallData) => {
                        if (d.type === 'total') {
                            return '';
                        }
                        if (d.percentage && d.percentage > 0.01) {
                            return `${d.percentage.toFixed(2)}%`;
                        }
                        return '';
                    })
                    .style('textAlign', 'center')
                    .style('textBaseline', 'bottom')
                    .style('dy', -20)
                    .style('fontSize', 11)
                    .style('fill', 'rgba(0, 0, 0, 0.45)')
                    .style('fontFamily', CHART_FONT_FAMILY_CSS);
            }
        }

        /**
         * One `link` mark for every connector rather than one `line` mark per pair. Because it encodes
         * on the same `index` band field as the bars, G2's scrollbar filter — which narrows the view's
         * x domain across every mark — windows the connectors in step with the bars. Per-pair line
         * marks carried their own data and would keep drawing across the whole domain while scrolled.
         * G2 offsets band-scaled link endpoints to the band midpoint, i.e. the slot midpoint.
         */
        if (showConnectors && connectorData.length > 0) {
            chart
                .link()
                .data(connectorData)
                .encode('x', ['index', 'nextIndex'])
                .encode('y', ['connectorStart', 'connectorEnd'])
                .style('stroke', connectorColor)
                .style('strokeWidth', 1)
                .style('lineDash', [4, 4])
                .tooltip(false);
        }

        // Handle bar clicks
        if (onBarClick) {
            chart.on('interval:click', (event: any) => {
                onBarClick(event.data?.data);
            });
        }

        chartInstance.current = chart;
        // forceFit after the first paint settles x-axis label wrap/ellipsis, which is measured
        // against the slot width and otherwise renders against a stale container width.
        chart.render().then(() => {
            chartInstance.current?.forceFit?.();
            detachAxisLabelTooltip.current?.();
            detachAxisLabelTooltip.current = attachAxisLabelHoverTooltip(chartInstance.current);
        });

        return () => {
            detachAxisLabelTooltip.current?.();
            detachAxisLabelTooltip.current = null;
            chart.destroy();
        };
    }, [
        processedData,
        connectorData,
        height,
        positiveColor,
        negativeColor,
        totalColor,
        connectorColor,
        showLabels,
        showConnectors,
        showPercentages,
        yAxisTitle,
        onBarClick,
        slotLayout,
        valueDomain,
    ]);

    return (
        <ConfigProvider theme={theme}>
            <div
                className={`w-full bg-white rounded-lg border border-[rgba(0,0,0,0.06)] ${className}`}
                data-test={props['data-test']}
                data-test-id={props['data-test-id']}
                data-asper-waterfall-chart
            >
                {/* Header Section */}
                <div className="p-5 border-b border-[rgba(0,0,0,0.06)]">
                    <div className="flex justify-between items-start mb-2">
                        <h3 className="font-['Open_Sans'] font-semibold text-[16px] leading-normal text-[rgba(0,0,0,0.88)] m-0">
                            {chartTitle}
                        </h3>
                        {onDownload && (
                            <Button
                                type="text"
                                icon={<DownloadFile size={16} />}
                                onClick={onDownload}
                                className="!p-2"
                                data-test-id={`${props['data-test-id']}-download-btn`}
                            />
                        )}
                    </div>

                    {description && (
                        <p className="font-['Open_Sans'] font-normal text-[14px] leading-[22px] text-[rgba(0,0,0,0.65)] m-0">
                            {description}
                        </p>
                    )}
                </div>

                {/* Filters Section */}
                {(showMetricDropdown || showDimensionDropdown || filters.length > 0) && (
                    <div className="px-5 pt-5 pb-3">
                        <div className="flex flex-wrap gap-4">
                            {showMetricDropdown && (
                                <div className="flex flex-col gap-1">
                                    <label className="font-['Open_Sans'] font-normal text-sm leading-[20px] text-[rgba(0,0,0,0.45)]">
                                        Metric
                                    </label>
                                    <Select
                                        value={selectedMetricIndex}
                                        onChange={(value) => setSelectedMetricIndex(Number(value))}
                                        options={metricOptions}
                                        className="min-w-[180px]"
                                        data-test-id={`${props['data-test-id']}-metric-filter`}
                                    />
                                </div>
                            )}
                            {showDimensionDropdown && (
                                <div className="flex flex-col gap-1">
                                    <label className="font-['Open_Sans'] font-normal text-sm leading-[20px] text-[rgba(0,0,0,0.45)]">
                                        Breakdown
                                    </label>
                                    <Select
                                        value={selectedDimension}
                                        onChange={(value) => setSelectedDimension(value as WaterfallDimension)}
                                        options={dimensionOptions.map((option) => ({
                                            label: option,
                                            value: option,
                                        }))}
                                        className="min-w-[180px]"
                                        data-test-id={`${props['data-test-id']}-dimension-filter`}
                                    />
                                </div>
                            )}
                            {filters.map((filter, index) => (
                                <div key={index} className="flex flex-col gap-1">
                                    <label className="font-['Open_Sans'] font-normal text-sm leading-[20px] text-[rgba(0,0,0,0.45)]">
                                        {filter.label}
                                    </label>
                                    <Select
                                        value={filter.defaultValue}
                                        onChange={filter.onChange}
                                        options={filter.options}
                                        className="min-w-[180px]"
                                        data-test-id={`${props['data-test-id']}-filter-${index}`}
                                    />
                                </div>
                            ))}
                        </div>
                    </div>
                )}

                {/* Y-Axis Label */}
                {yAxisTitle && (
                    <div className="px-5 pt-3">
                        <p className="font-['Open_Sans'] font-normal text-sm leading-[20px] text-[rgba(0,0,0,0.45)] m-0">
                            {yAxisTitle}
                        </p>
                    </div>
                )}

                {/* Chart Section */}
                <div className={`px-5 py-5 ${chartClassName}`} style={{ height: `${height}px` }}>
                    <div ref={chartContainer} style={{ width: '100%', height: '100%' }} />
                </div>

                {/* Footer Section */}
                {footerLabel && (
                    <div className="px-5 pb-5 flex justify-center items-center gap-2">
                        <span className="font-['Open_Sans'] font-semibold text-[14px] leading-[22px] text-[rgba(0,0,0,0.88)]">
                            {footerLabel}
                        </span>
                        {showFooterArrow && <ArrowRight size={16} color="rgba(0,0,0,0.88)" />}
                    </div>
                )}
            </div>
        </ConfigProvider>
    );
};

export default WaterfallChart;
